import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { IsActiveMatchOptions } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'fuse-vertical-navigation-basic-item',
    templateUrl: './basic.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuseVerticalNavigationBasicItemComponent
    implements OnInit, OnDestroy
{
    @Input() item: FuseNavigationItem;
    @Input() name: string;

    isActiveMatchOptions: IsActiveMatchOptions;
    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    currentLang: string;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseUtilsService: FuseUtilsService,
        private translate: TranslateService,
    ) {
        // Set the equivalent of {exact: false} as default for active match options.
        // We are not assigning the item.isActiveMatchOptions directly to the
        // [routerLinkActiveOptions] because if it's "undefined" initially, the router
        // will throw an error and stop working.
        this.isActiveMatchOptions = this._fuseUtilsService.subsetMatchOptions;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.initializeLanguage();

        // Lógica de Match Options
        this.isActiveMatchOptions =
            (this.item.isActiveMatchOptions ?? this.item.exactMatch)
                ? this._fuseUtilsService.exactMatchOptions
                : this._fuseUtilsService.subsetMatchOptions;

        // 1. Intentar obtener el componente padre
        this._fuseVerticalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // 2. VALIDACIÓN CRÍTICA: Solo suscribirse si el componente existe
        if (this._fuseVerticalNavigationComponent) {
            this._fuseVerticalNavigationComponent.onRefreshed
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this._changeDetectorRef.markForCheck();
                });
        } else {
            // Si no existe, imprimimos un aviso amigable en lugar de un error fatal
            console.warn(
                `FuseNavigation: No se encontró el contenedor '${this.name}' para el item '${this.item.title}'.`,
            );
        }

        this._changeDetectorRef.markForCheck();
    }

    initializeLanguage() {
        // 1. Intentar obtener el idioma guardado
        const savedLang = localStorage.getItem('lang');
        this.translate.setDefaultLang('es'); // Establecer 'es' como idioma principal

        let finalLang: string;

        if (savedLang) {
            // Caso 1: Usar el idioma guardado
            finalLang = savedLang;
        } else {
            // Caso 2: Usar el idioma del navegador si es 'es' o 'en'
            const browserLang = this.translate.getBrowserLang();
            finalLang =
                browserLang && browserLang.match(/es|en/) ? browserLang : 'es';
        }

        this.translate.use(finalLang);
        this.currentLang = finalLang; // Sincroniza la variable con el idioma usado
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
